import { ClassName, Theme } from '@busybusy/webapp-react-ui';
import { FlaggedTimeEntryLocationStatus } from '__generated__/graphql';
import { GpsIcon } from 'assets/icons';
import classNames from 'classnames';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardContentRibbon from 'components/foundation/dashboard/DashboardContentRibbon/DashboardContentRibbon';
import DashboardRibbonGroup from 'components/foundation/dashboard/DashboardRibbonGroup/DashboardRibbonGroup';
import useDashboardGraphDates from 'containers/dashboard/hooks/useDashboardGraphDates';
import { DashboardTimeFrame } from 'containers/dashboard/types/types';
import { convertDashboardTimeFrameToTimeRangeType, getDashboardTimeFrameTitle } from 'containers/dashboard/util/utils';
import { StatusOptions } from 'containers/gps-outside-project-report/GpsOutsideProjectReportSidePanel/GpsOutsideProjectReportSidePanel';
import useGpsOutsideProjectReportData, {
  IGpsOutsideProjectReportFilter,
} from 'containers/gps-outside-project-report/hooks/useGpsOutsideProjectReportData';
import { getDashboardNavigationRange } from 'containers/scheduling/utils/utils';
import { usePermissions } from 'hooks';
import useGPSOutsideProjectReportNavigation from 'hooks/navigation/useGPSOutsideProjectReportNavigation';
import { t } from 'i18next';
import { camelCase, isNil, last } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useFeatureFlags } from 'utils/features';
import DashboardProUpgradeCard from '../DashboardProUpgradeCard/DashboardProUpgradeCard';

export interface IPendingGPSOutsideProjectDashboardCardProps {
  onHide?: () => void;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  className?: ClassName;
}

const PendingGPSOutsideProjectDashboardCard = (props: IPendingGPSOutsideProjectDashboardCardProps) => {
  const { className, theme, onHide, setting } = props;

  const isPro = useFeatureFlags('PRO');

  const currentTimeFrame: DashboardTimeFrame = setting.options?.time ?? 'payPeriod';
  const { timeRange, timeRanges } = useDashboardGraphDates(currentTimeFrame);

  const reportFilter: IGpsOutsideProjectReportFilter = useMemo(() => {
    let range = timeRange;
    const lastRange = last(timeRanges);

    if (!isNil(lastRange)) {
      range = {
        startTime: DateTime.fromSeconds(lastRange.startTime, { zone: 'utc' }),
        endTime: DateTime.fromSeconds(lastRange.endTime, { zone: 'utc' }),
      };
    }

    return {
      timeRange: range,
      memberId: null,
      status: null,
      currentTimeRangeType: undefined,
      reviewdByMemberId: null,
      projectId: null,
      employeeGroupId: null,
      projectGroupId: null,
    };
  }, [timeRanges, timeRange]);

  const fetchGpsOutsideProjectData = useGpsOutsideProjectReportData(reportFilter);
  const { hasPermissionToManage } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [pendingReview, setPendingReview] = useState<string>('0');
  const [authorized, setAuthorized] = useState<string>('0');
  const [unauthorized, setUnauthorized] = useState<string>('0');
  const navigateToReport = useGPSOutsideProjectReportNavigation();

  useEffect(() => {
    setLoading(true);
    getPendingTasksTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTimeFrame]);

  function getPendingTasksTotal() {
    fetchGpsOutsideProjectData().then((results) => {
      const permissionScopedData = results.data.filter((item) =>
        hasPermissionToManage(item.member, 'manageGpsApproval')
      );

      let authorizedCount = 0;
      let unauthorizedCount = 0;
      let pendingReviewCount = 0;

      permissionScopedData.forEach((item) => {
        if (!isNil(item.status)) {
          const camelCaseStatus = camelCase(item.status);
          if (camelCaseStatus === FlaggedTimeEntryLocationStatus.Authorized) {
            authorizedCount += 1;
          } else if (camelCaseStatus === FlaggedTimeEntryLocationStatus.Unauthorized) {
            unauthorizedCount += 1;
          } else {
            pendingReviewCount += 1;
          }
        } else {
          pendingReviewCount += 1;
        }
      });
      setAuthorized(authorizedCount.toString());
      setUnauthorized(unauthorizedCount.toString());
      setPendingReview(pendingReviewCount.toString());

      setLoading(false);
    });
  }

  function handleNavigation(status: StatusOptions) {
    const navRange = getDashboardNavigationRange(timeRange, timeRanges, setting.options?.time ?? 'payPeriod', 'utc');

    navigateToReport({
      start_date: navRange.startTime.toISODate(),
      end_date: navRange.endTime.toISODate(),
      time_range_type: convertDashboardTimeFrameToTimeRangeType(setting.options?.time ?? 'payPeriod'),
      flagged_location_status: status,
    });
  }

  const classes = classNames('pending-gps-outside-project-dashboard-card', className);

  return (
    <>
      {isPro ? (
        <DashboardRibbonGroup className={classes}>
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => handleNavigation(StatusOptions.PENDING_REVIEW)}
            title={t('GPS Pending Review')}
            subtitle={getDashboardTimeFrameTitle('', currentTimeFrame)}
            value={pendingReview.toString()}
            isLoading={loading}
          />
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => handleNavigation(StatusOptions.AUTHORIZED)}
            title={t('GPS Authorized')}
            subtitle={getDashboardTimeFrameTitle('', currentTimeFrame)}
            value={authorized.toString()}
            isLoading={loading}
          />
          <DashboardContentRibbon
            theme={Theme.DARK}
            onClick={() => handleNavigation(StatusOptions.UNAUTHORIZED)}
            title={t('GPS Unauthorized')}
            subtitle={getDashboardTimeFrameTitle('', currentTimeFrame)}
            value={unauthorized.toString()}
            isLoading={loading}
          />
        </DashboardRibbonGroup>
      ) : (
        <DashboardProUpgradeCard title={setting.title} svg={GpsIcon} theme={theme} onHide={onHide} />
      )}
    </>
  );
};

export default PendingGPSOutsideProjectDashboardCard;
